import {inject, Injectable} from '@angular/core';
import {NavController} from '@ionic/angular';
import {environment} from "../../../environments/environment";
import {Router} from "@angular/router";
import {CompanyModel} from "../../models/company.model";
import {Observable, Subject} from "rxjs";

@Injectable({
  providedIn: 'root'
})

export class NavigationService {
  private navController: NavController = inject(NavController);
  private router: Router = inject(Router);

  private resetMarkerSubject: Subject<void> = new Subject<void>();
  public resetMarker$: Observable<void> = this.resetMarkerSubject.asObservable();


  private readonly standardHeaderRoutes: string[] = [
    '/app/leaf',
    '/app/view-post',
    '/app/team',
    '/app/profile',
    '/app/impact-map'
  ];

  private readonly momentsGalleryRoutes: string[] = [
    '/app/home/moments-gallery/'
  ]

  private readonly registrationRoutes: string[] = [
    '/login',
    '/register'
  ]

  public isStandardRoute(): boolean {
    return this.standardHeaderRoutes.includes(this.router.url);
  }

  public isRegistrationRoute(): boolean {
    return this.registrationRoutes.includes(this.router.url);
  }

  public isMomentsGalleryRoute(): boolean {
    return this.momentsGalleryRoutes.some(route => this.router.url.startsWith(route));
  }

  public async openCompanyProfile(company: CompanyModel): Promise<void> {
    const baseUrl = '/app/team/company/';
    const target = company.isAdmin ? 'view-company' : 'other-company';
    const path = `${baseUrl}${target}/${company.id}`;
    await this.navController.navigateForward([path]);
  }

  public async openCompanyProfileById(id: number, isAdmin: boolean): Promise<void> {
    const baseUrl = '/app/team/company/';
    const target = isAdmin ? 'view-company' : 'other-company';
    const path = `${baseUrl}${target}/${id}`;
    await this.navController.navigateForward([path]);
  }

  public async openTeamProfile(team: { id: number; isAdmin: boolean }): Promise<void> {
    const baseUrl: string = '/app/team/';
    let target: string;

    if (team.isAdmin) {
      target = 'view-team';
    } else {
      target = 'other-team';
    }
    const path = `${baseUrl}${target}/${team.id}`;
    await this.navController.navigateForward([path]);
  }

  public async openOtherUserProfile(id: number, isDeleted: boolean): Promise<void> {
    if (!isDeleted) {
      await this.navController.navigateForward(['/app/home/other-profile/' + id]);
    }
  }

  public openInteraction(item: { id: number }, type: string): Promise<boolean> {
    const path = `/app/home/interaction/${item?.id}/${type}`;
    return this.navController.navigateForward([path]);
  }

  public openHome() {
    this.navController.navigateRoot('/app/home');
  }

  public openNotifications() {
    this.navController.navigateForward(['/app/home/notifications']);
  }

  public openPostModal(type: string, id: number) {
    this.navController.navigateForward(['/app/home/post-modal/' + type + '/' + id]);
  }

  public async openActionPointDetail(id: number): Promise<void> {
    await this.navController.navigateForward(['/app/home/view-post/action_point/' + id + '/detail']);
  }

  public openSearchModal(): void {
    this.navController.navigateForward(['/app/home/search/all']);
  }

  public openImpactSuite() {
    switch (environment.name) {
      case 'dev': {
        window.open('https://planeed-dev.wunu.dev/cms-login');
        break;
      }
      case 'stage': {
        window.open('https://planeed-stage.wunu.dev/cms-login');
        break;
      }
      case 'prod': {
        window.open('https://app.planeed.app/cms-login');
        break;
      }
      default: {
        break;
      }
    }
  }

  public back(currentUrl?: string): void {
    const url = currentUrl ?? this.router.url;
    if (this.standardHeaderRoutes.includes(url)) {
      this.router.navigate(['/app/home']);
    } else {
      this.navController.back();
    }
  }

  public async openMapMoment(id: number, actionPointId?: number): Promise<void> {
    await this.navController.navigateForward([`app/map-moment/${id}`], {queryParams: {actionPointId: actionPointId}});
  }

  public async openPointOfActionDetails(id: number): Promise<void> {
    const replace: boolean = this.router.url.includes('point-of-action-details/');
    await this.navController.navigateForward([`app/point-of-action-details/${id}`], {replaceUrl: replace});
  }

  setCurrentTab(): string {
    const url = this.router.url;
    if (url.includes('home/search')) return 'search';
    else if (url.includes('post-modal')) return 'creation';
    else if (url.includes('home/notifications')) return 'notification';
    else if (url.includes('home')) return 'home';
    else if (url.includes('action-point')) return 'action-point';
    else if (url.includes('leaf')) return 'leaf';
    else if (url.includes('team')) return 'team';
    else if (url.includes('profile')) return 'profile';
    else if (url.includes('impact-map')) return 'impact-map';
  }

  public triggerResetMarker(): void {
    if (this.resetMarkerSubject) {
      this.resetMarkerSubject.next();
    }
  }
}
